import { useState } from 'react';
import { api } from '../api.ts';
import type { MeResponse } from '../../../shared/types.ts';

interface Props {
  me: MeResponse | null;
  onChanged: () => void | Promise<void>;
  onSignOut: () => void;
  onOpenAuth: () => void;
}

const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#84cc16', '#14b8a6'];

export function ProfileView({ me, onChanged, onSignOut, onOpenAuth }: Props): React.ReactElement {
  const [displayName, setDisplayName] = useState(me?.user?.displayName ?? '');
  const [avatarColor, setAvatarColor] = useState(me?.user?.avatarColor ?? COLORS[0]!);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(me?.settings.soundEnabled ?? true);
  const [reduceMotion, setReduceMotion] = useState(me?.settings.reduceMotion ?? false);

  async function saveProfile(): Promise<void> {
    if (!me?.user) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.setProfile(displayName.trim(), avatarColor);
      setMsg('Profile saved.');
      await onChanged();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function saveSettings(): Promise<void> {
    setBusy(true);
    try {
      await api.setSettings(soundEnabled, reduceMotion);
      setMsg('Settings saved.');
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="profile-page">
      <h2>Profile</h2>

      {me?.user ? (
        <div className="card">
          <h3>Account</h3>
          <p className="muted small">Signed in as <strong>{me.user.username}</strong></p>
          <label>
            Display name
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={30} />
          </label>
          <label>
            Avatar colour
            <div className="color-swatches">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`swatch ${avatarColor === c ? 'sel' : ''}`}
                  style={{ background: c }}
                  onClick={() => setAvatarColor(c)}
                  aria-label={`Choose ${c}`}
                />
              ))}
            </div>
          </label>
          <div className="row gap-sm">
            <button onClick={saveProfile} disabled={busy}>Save profile</button>
            <button className="ghost" onClick={onSignOut} disabled={busy}>Sign out</button>
          </div>
        </div>
      ) : (
        <div className="card">
          <h3>You're playing anonymously</h3>
          <p className="muted">
            Your score and streaks are stored on this browser. Sign in to keep them across devices and put your name on the global ladder.
          </p>
          <div className="row gap-sm">
            <button onClick={onOpenAuth}>Sign in / Create account</button>
          </div>
        </div>
      )}

      <div className="card">
        <h3>Settings</h3>
        <label className="check">
          <input type="checkbox" checked={soundEnabled} onChange={(e) => setSoundEnabled(e.target.checked)} />
          Sound effects
        </label>
        <label className="check">
          <input type="checkbox" checked={reduceMotion} onChange={(e) => setReduceMotion(e.target.checked)} />
          Reduce motion
        </label>
        <div className="row gap-sm">
          <button onClick={saveSettings} disabled={busy}>Save settings</button>
        </div>
      </div>

      {msg && <p className="small">{msg}</p>}
    </div>
  );
}
